import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { storage } from '../../utils/storage';

/**
 * FAB (Floating Action Button)
 * 우하단 플로팅 버튼 - 빠른 액션 + 드래그 가능
 */
export const FAB: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const fabRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // 초기 위치 설정 (우하단)
  useEffect(() => {
    const savedPosition = storage.get('fab-position');
    if (savedPosition) {
      setPosition(JSON.parse(savedPosition));
    } else {
      // 기본 위치: 우하단
      const defaultX = window.innerWidth - 48 - 16; // 우측 16px 여백
      const defaultY = window.innerHeight - 48 - 96 - 16; // 하단 네비(96px) + 16px 여백
      setPosition({ x: defaultX, y: defaultY });
    }
  }, []);

  const toggleMenu = () => {
    if (!isDragging) {
      setIsOpen(!isOpen);
    }
  };

  // 드래그 시작
  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    e.stopPropagation();
    if (!position) return;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    setIsDragging(true);
    setDragStart({ x: clientX - position.x, y: clientY - position.y });
  };

  // 드래그 중
  const handleDragMove = (e: TouchEvent | MouseEvent) => {
    if (!isDragging || !position) return;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    const newX = clientX - dragStart.x;
    const newY = clientY - dragStart.y;
    
    // 화면 경계 제한
    const maxX = window.innerWidth - 48;
    const maxY = window.innerHeight - 48 - 80;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  // 드래그 종료
  const handleDragEnd = () => {
    if (isDragging && position) {
      setIsDragging(false);
      storage.set('fab-position', JSON.stringify(position));
    }
  };

  // 드래그 이벤트 리스너
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, position, dragStart]);

  const actions = [
    {
      icon: '📅',
      label: '휴무 신청',
      onClick: () => {
        navigate('/scheduler');
        setIsOpen(false);
      },
      show: true,
    },
    {
      icon: '🎲',
      label: '로또 보기',
      onClick: () => {
        navigate('/lotto');
        setIsOpen(false);
      },
      show: location.pathname !== '/lotto',
    },
    {
      icon: '🏠',
      label: '홈으로',
      onClick: () => {
        navigate('/');
        setIsOpen(false);
      },
      show: location.pathname !== '/',
    },
  ].filter(action => action.show);

  // 위치가 null이면 로딩 중 (렌더링 안 함)
  if (!position) return null;

  return (
    <>
      {/* 배경 오버레이 */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* FAB 메뉴 */}
      <div
        ref={fabRef}
        className="fixed z-50 flex flex-col-reverse items-end gap-2"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          cursor: isDragging ? 'grabbing' : 'grab',
          transition: isDragging ? 'none' : 'all 0.2s',
        }}
      >
        {/* 액션 버튼들 */}
        {isOpen && actions.map((action, index) => (
          <button
            key={action.label}
            onClick={action.onClick}
            className="flex items-center gap-2 bg-white rounded-full shadow-lg pl-3 pr-4 py-2 hover:shadow-xl transition-all duration-200 animate-bounceIn"
            style={{
              animationDelay: `${index * 50}ms`,
            }}
          >
            <span className="text-xl">{action.icon}</span>
            <span className="font-medium text-gray-700 whitespace-nowrap text-sm">
              {action.label}
            </span>
          </button>
        ))}

        {/* 메인 FAB 버튼 (더 작게 + 반투명) */}
        <button
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onClick={toggleMenu}
          className={`bg-gradient-to-r from-orange-400 to-orange-500 text-white w-12 h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center opacity-70 hover:opacity-100 ${
            isOpen ? 'rotate-45 opacity-100' : ''
          } ${isDragging ? 'scale-110 opacity-100' : ''}`}
        >
          <span className="text-xl">{isOpen ? '✕' : '🧡'}</span>
        </button>
      </div>
    </>
  );
};
